import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import styles from './AISummaryDetails.module.scss';
import { AISummaryDetailsProps } from './types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { ActionButtons } from '../action-buttons/ActionButtons';
import { AISummaryStartForm } from './components/AISummaryStartForm';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAISummary } from '../../hooks/useAISummary';
import { useAISummaryDialog } from '../../../../common/ai-summary-dialog/useAISummaryDialog';
import AISummaryDialog from '../../../../common/ai-summary-dialog/AISummaryDialog';
import { PacmanLoaderBlock } from '../../../../common/pacman-loader';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { RootState } from '../../../../store';
import { ROUTES } from '../../../../common/constants/routes';
import { sanitizeRedirectUrl } from '../../../../common/helpers/sanitize';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';

const AISummaryDetails = ({
  pendingStatus,
  downloadDocument,
  signDocument,
  editDocument,
  createTemplate,
  deleteDocument,
  doc,
  allowGettingAISummary,
  isImage,
}: AISummaryDetailsProps) => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const location = useLocation();
  const path = location.pathname;
  const contractGenerated = params.get('contractGenerated');
  const generateSummaryParam = params.get('generateSummary');
  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();
  const { documentId } = useParams();
  const aiSummaryGenerated = useAppSelector(
    (state: RootState) => state.currentDocumentAISummary.summary
  );
  const aiAssistProp = contractGenerated
    ? 'open_ai_assist_document_contract'
    : 'open_ai_assist_document_upload';
  const aiAssistId = doc && doc[aiAssistProp]?.length > 0 ? Math.max(...doc[aiAssistProp]) : null;
  const navigate = useNavigate();
  const {
    summary,
    pendingStatus: pendingStatusAISummary,
    aiSummaryCreationMode,
    setAISummaryCreationMode,
  } = useAISummary(aiAssistId, allowGettingAISummary);
  const {
    dialogOpen: aiSummaryDialogOpen,
    setDocument: setDocumentForAISummary,
    handleAnalyze: handleAnalyzeFunction,
    documentType,
    setDocumentType,
    purpose,
    setPurpose,
    purposes,
    resultFormat,
    setResultFormat,
    contextualInformation,
    setContextualInformation,
    simplifyJargon,
    setSimplifyJargon,
    detailLevel,
    setDetailLevel,
    analysisLoading,
    setAnalysisLoading,
  } = useAISummaryDialog();
  return (
    <>
      <Box className={styles.rightSection}>
        <Box className={styles.aiSummaryHeader}>
          <Typography className={styles.aiSummaryTitle} variant='h6'>
            {t('headers.aiSummary')}
          </Typography>
          <Box className={styles.headerActions}>
            {doc && doc.signing_doc_id && !isImage && !doc.is_flattened && (
              <Button
                type='button'
                className='btn-default btn-action-basic btn-small btn-green'
                role='button'
                variant='contained'
                onClick={() =>
                  redirectToDocumenso(
                    `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                  )
                }
                style={{ marginRight: '8px' }}
              >
                {doc.status === 'COMPLETED'
                  ? t('buttons.signedDocument')
                  : t('buttons.documentInSigning')}
              </Button>
            )}
            <ActionButtons
              isImage={isImage}
              handleAISummaryGeneration={() => setAISummaryCreationMode(true)}
              handleDocumentDownload={downloadDocument}
              handleSignDocument={doc && doc.signing_doc_id ? null : signDocument}
              handleEditDocument={doc && doc.is_flattened ? null : editDocument}
              handleCreateTemplate={doc && doc.is_flattened ? null : createTemplate}
              handleDeleteDocument={deleteDocument}
              isDocumentContentTab={false}
              doc={doc}
            />
          </Box>
        </Box>
        <Box className={styles.aiSummaryContent}>
          {(summary || aiSummaryGenerated) && !aiSummaryCreationMode ? (
            parse(aiSummaryGenerated?.content || summary)
          ) : !summary && !aiSummaryCreationMode ? (
            <AISummaryStartForm
              buttonDisabled={isImage}
              handleBtnClick={() => setAISummaryCreationMode(true)}
            />
          ) : null}
          {aiSummaryCreationMode && (
            <AISummaryDialog
              open={aiSummaryDialogOpen}
              //documentName={documentForAISummary.name}
              onClose={() => {
                setDocumentForAISummary(null);
                setAISummaryCreationMode(false);
                if (generateSummaryParam) {
                  navigate(window.location.pathname + '?initialTab=1');
                }
              }}
              simplifyJargon={simplifyJargon}
              setSimplifyJargon={setSimplifyJargon}
              setDocumentType={setDocumentType}
              documentType={documentType}
              resultFormat={resultFormat}
              setResultFormat={setResultFormat}
              purpose={purpose}
              setPurpose={setPurpose}
              purposes={purposes}
              contextualInformation={contextualInformation}
              setContextualInformation={setContextualInformation}
              detailLevel={detailLevel}
              setDetailLevel={setDetailLevel}
              handleAnalyzeFunction={() =>
                handleAnalyzeFunction({
                  handleAnalyzeSuccess: () => {
                    setAISummaryCreationMode(false);
                    setDocumentForAISummary(null);
                    setAnalysisLoading(false);
                  },
                  handleAnalyzeError: undefined,
                  isDocumentContract: Boolean(contractGenerated),
                  documentId,
                })
              }
            />
          )}
          {analysisLoading && <PacmanLoaderBlock loading={analysisLoading} />}
        </Box>
        {(pendingStatus || pendingStatusAISummary) && (
          <ProgressOverlay className='position-absolute' />
        )}
      </Box>
    </>
  );
};

export default AISummaryDetails;
